import { forwardRef, useCallback, useContext, useMemo, useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '../../lib/styles';
import { PopoverContext, PopoverTriggerContext } from './Popover.context';

export type PopoverProps = React.ComponentProps<typeof PopoverPrimitive.Root>;

export type PopoverTriggerProps = React.ComponentProps<typeof PopoverPrimitive.Trigger>;

function Popover({ open: controlledOpen, onOpenChange, ...props }: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(controlledOpen);
  const open = controlledOpen ?? internalOpen;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setInternalOpen(newOpen);
      return onOpenChange?.(newOpen);
    },
    [onOpenChange],
  );

  const contextValue = useMemo(() => ({ open, onOpenChange: handleOpenChange }), [handleOpenChange, open]);

  return (
    <PopoverContext.Provider value={contextValue}>
      <PopoverPrimitive.Root data-slot="popover" open={open} onOpenChange={handleOpenChange} {...props} />
    </PopoverContext.Provider>
  );
}

function PopoverTrigger(props: PopoverTriggerProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverContext = useContext(PopoverContext);
  const contextValue = useMemo(
    () => ({
      ...popoverContext,
      triggerRef,
    }),
    [popoverContext],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        popoverContext?.onOpenChange?.(!popoverContext.open);
      }

      props.onKeyDown?.(event);
    },
    [popoverContext, props],
  );

  return (
    <PopoverTriggerContext.Provider value={contextValue}>
      <PopoverPrimitive.Trigger
        data-slot="popover-trigger"
        data-popover-trigger
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        {...props}
      />
    </PopoverTriggerContext.Provider>
  );
}

function PopoverClose({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Close>) {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
}

export type PopoverContentProps = React.ComponentProps<typeof PopoverPrimitive.Content> & {
  naked?: boolean;
  withinPortal?: boolean;
};

const PopoverContent = forwardRef(
  (
    { className, align = 'center', sideOffset = 4, naked = false, withinPortal = false, ...rest }: PopoverContentProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const content = (
      <PopoverPrimitive.Content
        ref={ref}
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        updatePositionStrategy="always"
        className={cn(
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-72 origin-(--radix-popover-content-transform-origin)',
          {
            'bg-white text-text-primary rounded-2xl border p-4 shadow-md outline-hidden': !naked,
            'dark:bg-black/80 dark:text-white dark:border-black/80': !naked,
          },
          className,
        )}
        collisionPadding={16}
        {...rest}
      />
    );

    if (!withinPortal) return content;

    return <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal>;
  },
);

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, PopoverClose };
